/**
 * Mock Curriculum Plans
 */

import type { CpCurriculumPlan } from '../../../types/curriculum_planning';

const now = Date.now();

// Helper to generate curriculum plans for a year group
const generateYearGroupPlans = (
  yearGroupId: string,
  academicYearId: string,
  isKS4: boolean
): CpCurriculumPlan[] => {
  const plans: CpCurriculumPlan[] = [];
  const prefix = yearGroupId.replace('yg-', 'cp-');

  // Core subjects - all year groups
  plans.push(
    {
      id: `${prefix}-maths`,
      subjectId: 'subj-maths',
      yearGroupId,
      academicYearId,
      lessonsPerCycle: isKS4 ? 8 : 6,
      lessonsPerWeek: isKS4 ? 8 : 6,
      lessonLength: 'single',
      maxLessonsPerDay: 2,
      capacitySplitRequired: true,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: `${prefix}-english`,
      subjectId: 'subj-english',
      yearGroupId,
      academicYearId,
      lessonsPerCycle: isKS4 ? 8 : 6,
      lessonsPerWeek: isKS4 ? 8 : 6,
      lessonLength: 'single',
      maxLessonsPerDay: 2,
      capacitySplitRequired: true,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: `${prefix}-science`,
      subjectId: 'subj-science',
      yearGroupId,
      academicYearId,
      lessonsPerCycle: isKS4 ? 10 : 6,
      lessonsPerWeek: isKS4 ? 10 : 6,
      lessonLength: 'double',
      doublesPerCycle: isKS4 ? 5 : 3,
      preferredRoomTypeId: 'rt-science',
      capacitySplitRequired: true,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    }
  );

  // Statutory subjects
  plans.push(
    {
      id: `${prefix}-pe`,
      subjectId: 'subj-pe',
      yearGroupId,
      academicYearId,
      lessonsPerCycle: 4,
      lessonsPerWeek: 4,
      lessonLength: 'double',
      doublesPerCycle: 2,
      preferredRoomTypeId: 'rt-sports',
      capacitySplitRequired: false,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    },
    {
      id: `${prefix}-pshe`,
      subjectId: 'subj-pshe',
      yearGroupId,
      academicYearId,
      lessonsPerCycle: 1,
      lessonsPerWeek: 1,
      lessonLength: 'single',
      capacitySplitRequired: false,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    }
  );

  if (!isKS4) {
    // KS3-only subjects
    plans.push(
      {
        id: `${prefix}-history`,
        subjectId: 'subj-history',
        subjectSlotId: 'slot-humanities',
        yearGroupId,
        academicYearId,
        lessonsPerCycle: 2,
        lessonsPerWeek: 2,
        lessonLength: 'single',
        capacitySplitRequired: false,
        status: 'active',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: `${prefix}-geography`,
        subjectId: 'subj-geography',
        subjectSlotId: 'slot-humanities',
        yearGroupId,
        academicYearId,
        lessonsPerCycle: 2,
        lessonsPerWeek: 2,
        lessonLength: 'single',
        capacitySplitRequired: false,
        status: 'active',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: `${prefix}-french`,
        subjectId: 'subj-mfl-french',
        subjectSlotId: 'slot-mfl',
        yearGroupId,
        academicYearId,
        lessonsPerCycle: 3,
        lessonsPerWeek: 3,
        lessonLength: 'single',
        capacitySplitRequired: false,
        status: 'active',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: `${prefix}-art`,
        subjectId: 'subj-art',
        subjectSlotId: 'slot-creative',
        yearGroupId,
        academicYearId,
        lessonsPerCycle: 2,
        lessonsPerWeek: 2,
        lessonLength: 'double',
        doublesPerCycle: 1,
        preferredRoomTypeId: 'rt-art',
        rotationGroupId: `rg-${yearGroupId.replace('yg-', 'y')}-creative`,
        capacitySplitRequired: false,
        status: 'active',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: `${prefix}-music`,
        subjectId: 'subj-music',
        subjectSlotId: 'slot-creative',
        yearGroupId,
        academicYearId,
        lessonsPerCycle: 2,
        lessonsPerWeek: 2,
        lessonLength: 'single',
        preferredRoomTypeId: 'rt-music',
        rotationGroupId: `rg-${yearGroupId.replace('yg-', 'y')}-creative`,
        capacitySplitRequired: false,
        status: 'active',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: `${prefix}-dt`,
        subjectId: 'subj-dt',
        yearGroupId,
        academicYearId,
        lessonsPerCycle: 2,
        lessonsPerWeek: 2,
        lessonLength: 'double',
        doublesPerCycle: 1,
        preferredRoomTypeId: 'rt-dt',
        capacitySplitRequired: false,
        status: 'active',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: `${prefix}-computing`,
        subjectId: 'subj-computing',
        yearGroupId,
        academicYearId,
        lessonsPerCycle: 2,
        lessonsPerWeek: 2,
        lessonLength: 'single',
        preferredRoomTypeId: 'rt-ict',
        capacitySplitRequired: false,
        status: 'active',
        createdAt: now,
        updatedAt: now,
      },
      {
        id: `${prefix}-re`,
        subjectId: 'subj-re',
        yearGroupId,
        academicYearId,
        lessonsPerCycle: 1,
        lessonsPerWeek: 1,
        lessonLength: 'single',
        capacitySplitRequired: false,
        status: 'active',
        createdAt: now,
        updatedAt: now,
      }
    );
  }

  return plans;
};

export const mockCpCurriculumPlans: CpCurriculumPlan[] = [
  // KS3 curriculum plans
  ...generateYearGroupPlans('yg-7', 'ay-2024-2025', false),
  ...generateYearGroupPlans('yg-8', 'ay-2024-2025', false),
  ...generateYearGroupPlans('yg-9', 'ay-2024-2025', false),
  // KS4 curriculum plans (core only, options handled by option blocks)
  ...generateYearGroupPlans('yg-10', 'ay-2024-2025', true),
  ...generateYearGroupPlans('yg-11', 'ay-2024-2025', true),
];
